import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import cookie from "react-cookies";

import { ApiCallResult, useAPI } from "services/Api";
import Input from "input/Input";
import Page from "pages/Page";
import ProfileInput from "input/ProfileInput";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";
import { ModularHeaderConfig } from "./ModularHeader";

const PatientEditor = ({ componentName = "[PatientEditor]" }) => {
    const [clinicianId, setClinicianId] = useState(undefined);
    const [clinicians, setClinicians] = useState(null);
    const [email, setEmail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [loadedProfile, setLoadedProfile] = useState(null);
    const [profile, setProfile] = useState(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const patientId = Number(useParams().id);

    // ---=== Utilities ===--- //

    const goToPatient = (id: number) => {
        void navigate(`/patient/${id}/usage`);
    };

    const checkIfClinician = () => {
        if (cookie.load("role") === "Physician") {
            let userId = cookie.load("user").pk;
            let clinician = clinicians.find((c) => c.id == userId);
            if (clinician) {
                setClinicianId(clinician.role_id);
            }
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!email) {
            notificationManager.addError("Please provide Patient email");
            return;
        }
        let data = {
            email: email,
            first_name: firstName,
            last_name: lastName,
            profile: profile,
            clinician: clinicianId
        };

        if (patientId) {
            void updateData(JSON.stringify(data));
        }
    };

    // ---=== KarunaCLOUD ===--- //

    const getClinicians = async() => {
        if (cookie.load("role") !== "Coach" && cookie.load("role") !== "Facilitator") {
            await API.api_Get(
                componentName,
                "/physicians/",
                notificationManager,
                (response) => {
                    setClinicians(response.data.results);
                }
            );
        }
    };

    const getPatientData = async() => {
        const result: ApiCallResult = await API.api_Get(
            componentName,
            `/patients/${patientId}`,
            notificationManager,
            (response) => {
                setFirstName(response.data.first_name);
                setLastName(response.data.last_name);
                setEmail(response.data.email);
                if (cookie.load("role") !== "Coach") {
                    setClinicianId(response.data.physician_role_id);
                }
                setLoadedProfile({
                    height: Number(response.data.profile.height),
                    gender: Number(response.data.profile.gender),
                    language: Number(response.data.profile.language),
                    payer_type: Number(response.data.profile.payer_type)
                });
            }
        );

        setIsLoading(!(result.wasSuccessful && result.wasCallable));
    };

    const updateData = async(data) => {
        const result: ApiCallResult = await API.api_Put(
            componentName,
            `/patients/${patientId}/`,
            data,
            notificationManager,
            (_response) => {
                goToPatient(patientId);
            },
            (error) => {
                notificationManager.addError(error);
            }
        );

        setIsLoading(!(result.wasSuccessful && result.wasCallable));
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (patientId) {
                void getPatientData();
            }
            void getClinicians();
        }, []
    );

    useEffect(
        () => {
            if (clinicians != null) {
                checkIfClinician();
            }
        }, [clinicians]
    );

    useEffect(
        () => {
            setProfile(loadedProfile);
        }, [loadedProfile]
    );

    // ---=== Render ===--- //

    const getModularHeaderConfig = (): ModularHeaderConfig => {
        return {
            trail: breadCrumbLinks(),
            body: {
                name: `${firstName} ${lastName}`,
                buttons: [
                    {
                        title: "back",
                        click: () => goToPatient(patientId),
                        img: "/icons/flip-backward.svg"
                    }
                ]
            }
        };
    };

    const breadCrumbLinks = () => {
        if (patientId) {
            return [
                { label: "Patients", url: "/" },
                { label: firstName !== "" ? `${firstName} ${lastName}` : "Patient", url: `/patient/${patientId}/usage` },
                { label: "Edit Patient", url: `/patient/${patientId}/edit/` }
            ];
        }
        else {
            return [
                { label: "Patients", url: "/patients" },
                { label: "Create patient", url: "/create-patient" }
            ];
        }
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={getModularHeaderConfig()}
        >
            {
                !isLoading
                    ? (
                        <form onSubmit={handleSubmit}>
                            <div className="w-full flex-col items-start gap-5 m-0 pl-6 pt-6 inline-flex">
                                <div className="w-1/3 flex-col justify-start items-start gap-8 flex">
                                    <div className="self-stretch h-[540px] flex-col justify-start items-start gap-6 flex">
                                        <div className="self-stretch justify-start items-start gap-8 inline-flex">
                                            <div className="grow w-full shrink basis-0 flex-col justify-start items-start gap-1.5 inline-flex">
                                                <div className="self-auto w-full h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="text-slate-700 text-sm w-full font-medium leading-tight">First Name</div>
                                                    <Input
                                                        className="self-auto text-gray-500 text-base w-full  font-normal  px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                        name="First Name"
                                                        required
                                                        type="text"
                                                        validations={[{ type: "minLength", value: 2 }, { type: "maxLength", value: 30 }]}
                                                        value={firstName}
                                                        // Callbacks
                                                        onChange={setFirstName}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grow shrink w-full basis-0 flex-col justify-start items-start gap-1.5 inline-flex">
                                                <div className="self-stretch h-[70px] w-full flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="text-gray-700 text-sm w-full font-medium leading-tight">Last Name</div>
                                                    <Input
                                                        className="self-auto text-gray-500 text-base font-normal  w-full  px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                        name="Last Name"
                                                        required
                                                        type="text"
                                                        validations={[{ type: "minLength", value: 2 }, { type: "maxLength", value: 30 }]}
                                                        value={lastName}
                                                        // Callbacks
                                                        onChange={setLastName}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                            <div className="self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Email</div>
                                                <Input
                                                    className="self-stretch text-gray-500 text-base font-normal px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                    name="Email"
                                                    required
                                                    type="text"
                                                    validations={[{ type: "email", value: email }]}
                                                    value={email}
                                                    // Callbacks
                                                    onChange={setEmail}
                                                />
                                            </div>
                                        </div>
                                        <ProfileInput
                                            loadedProfile={loadedProfile}
                                            // Callbacks
                                            setProfile={setProfile}
                                        />
                                    </div>
                                    <div className="self-stretch h-12 flex-col justify-start items-start gap-4 flex">
                                        <button className="self-stretch px-[18px] py-3 bg-brand-600 hover:bg-brand-700 rounded-lg shadow border border-brand-600 justify-center items-center gap-1.5 inline-flex cursor-pointer">
                                            <div className="px-0.5 justify-center items-center flex">
                                                <div className="text-white text-base font-semibold leading-normal">
                                                    Save Patient Info
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )
                    : (
                        renderLoader("Loading Patient")
                    )
            }
        </Page>
    );
};

export default PatientEditor;
